/**
 * EngagementsContrats — Liste contrats fournisseurs récurrents (parité BE-TAYE ContratsList).
 * Filtres 13 catégories, statuts, tri, search, badges urgence, bulk delete.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, ChevronRight, Filter, Clock, ArrowUpDown, Trash2, Upload, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_META, CATEGORIES, getCategoryMeta, fmtEur, fmtDate } from "@/lib/engagements-categories";

const StatusBadge = ({ statut, urgence }) => {
  if (statut === "resilier") return <Badge className="bg-slate-200 text-slate-700">Résilié</Badge>;
  if (statut === "resiliation_en_cours") return <Badge className="bg-orange-500 text-white">Résiliation en cours</Badge>;
  if (statut === "expire") return <Badge className="bg-red-100 text-red-700">Expiré</Badge>;
  const map = {
    critique: { c: "bg-red-100 text-red-700", l: "Urgent" },
    urgent: { c: "bg-orange-100 text-orange-700", l: "Attention" },
    attention: { c: "bg-yellow-100 text-yellow-700", l: "À surveiller" },
    ok: { c: "bg-emerald-100 text-emerald-700", l: "Actif" },
    depasse: { c: "bg-red-600 text-white", l: "Dépassé" },
  };
  const v = map[urgence] || map.ok;
  return <Badge className={v.c}>{v.l}</Badge>;
};

export default function EngagementsContrats() {
  const navigate = useNavigate();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statutFilter, setStatutFilter] = useState("all");
  const [sortBy, setSortBy] = useState("preavis");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/engagements/contrats");
      setContrats(r.data.contrats || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = [...contrats];
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(c => (c.nom_contrat + " " + c.prestataire).toLowerCase().includes(s));
    }
    if (catFilter !== "all") arr = arr.filter(c => c.categorie === catFilter);
    if (statutFilter !== "all") {
      if (statutFilter === "urgent") arr = arr.filter(c => ["critique", "urgent"].includes(c.urgence));
      else arr = arr.filter(c => (c.statut || "actif") === statutFilter);
    }
    const cmp = { date: (a,b) => (b.created_at||"").localeCompare(a.created_at||""),
                    montant: (a,b) => (b.montant_mensuel_ht||0) - (a.montant_mensuel_ht||0),
                    echeance: (a,b) => (a.date_echeance||"9999").localeCompare(b.date_echeance||"9999"),
                    preavis: (a,b) => (a.date_limite_preavis||"9999").localeCompare(b.date_limite_preavis||"9999"),
                    nom: (a,b) => (a.nom_contrat||"").localeCompare(b.nom_contrat||"") };
    return arr.sort(cmp[sortBy] || cmp.preavis);
  }, [contrats, search, catFilter, statutFilter, sortBy]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Supprimer ${selected.size} contrat(s) ?`)) return;
    try {
      await api.post("/engagements/contrats/bulk-delete", { ids: [...selected] });
      toast.success(`${selected.size} contrat(s) supprimé(s)`);
      setSelected(new Set()); setSelectMode(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const [bulkLinking, setBulkLinking] = useState(false);
  const bulkLinkVehicles = async () => {
    const ids = selected.size ? [...selected] : [];
    const msg = ids.length
      ? `Synchroniser ${ids.length} contrat(s) sélectionné(s) avec le Parc Auto ?`
      : "Synchroniser TOUS les contrats véhicule non liés avec le Parc Auto ?";
    if (!window.confirm(msg)) return;
    setBulkLinking(true);
    try {
      const r = await api.post("/engagements/contrats/bulk-link-vehicles", { ids });
      const { scanned, linked, created, skipped, errors } = r.data;
      toast.success(`✅ ${linked} lié(s) · ${created} véhicule(s) créé(s) · ${skipped} ignoré(s) · ${errors} erreur(s) sur ${scanned}`);
      setSelected(new Set()); setSelectMode(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkLinking(false); }
  };

  return (
    <div className="space-y-6" data-testid="engagements-contrats-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Contrats fournisseurs</h1>
          <p className="text-sm text-slate-500">{filtered.length} contrat{filtered.length > 1 ? "s" : ""} · engagements récurrents</p>
        </div>
        <div className="flex gap-2">
          {selectMode ? (
            <>
              <Button variant="outline" onClick={() => { setSelectMode(false); setSelected(new Set()); }}>Annuler</Button>
              <Button variant="outline" onClick={bulkLinkVehicles} disabled={bulkLinking} data-testid="bulk-link-btn">
                {bulkLinking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}Lier au Parc Auto ({selected.size || "tous"})
              </Button>
              <Button variant="destructive" onClick={bulkDelete} disabled={!selected.size} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selected.size})
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={bulkLinkVehicles} disabled={bulkLinking} data-testid="bulk-link-all-btn">
                {bulkLinking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}Lier tous au Parc Auto
              </Button>
              <Button variant="outline" onClick={() => setSelectMode(true)} data-testid="select-mode-btn"><Trash2 className="w-4 h-4 mr-2" />Sélection</Button>
              <Button variant="outline" onClick={() => navigate("/app/depenses/import")} data-testid="import-btn">
                <Upload className="w-4 h-4 mr-2" />Importer OCR
              </Button>
              <Button onClick={() => navigate("/app/depenses/contrats/nouveau")} data-testid="new-contrat-btn">
                <Plus className="w-4 h-4 mr-2" />Nouveau
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-input" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-[180px]" data-testid="cat-filter"><Filter className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statutFilter} onValueChange={setStatutFilter}>
            <SelectTrigger className="w-[180px]" data-testid="statut-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="actif">Actif</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="resiliation_en_cours">Résiliation en cours</SelectItem>
              <SelectItem value="resilier">Résilié</SelectItem>
              <SelectItem value="expire">Expiré</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px]" data-testid="sort-select"><ArrowUpDown className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="preavis">Préavis</SelectItem>
              <SelectItem value="echeance">Échéance</SelectItem>
              <SelectItem value="montant">Montant</SelectItem>
              <SelectItem value="date">Date d'ajout</SelectItem>
              <SelectItem value="nom">Nom</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center p-8 text-slate-500">Chargement...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Upload className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-500 mb-4">Aucun contrat enregistré</p>
          <Button onClick={() => navigate("/app/depenses/import")}><Plus className="w-4 h-4 mr-2" />Importer votre premier contrat</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((c, i) => {
            const meta = getCategoryMeta(c.categorie);
            const Icon = meta.icon;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer border-slate-200" onClick={() => !selectMode && navigate(`/app/depenses/contrats/${c.id}`)}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {selectMode && (
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} onClick={e => e.stopPropagation()} data-testid={`select-${c.id}`} />
                    )}
                    <div className={`w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-5 h-5 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{c.prestataire} — {c.nom_contrat}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {meta.label} · Échéance {fmtDate(c.date_echeance)} · Préavis {c.delai_preavis_jours || "?"}j
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold font-mono text-slate-900">{fmtEur(c.montant_mensuel_ht)}<span className="text-xs text-slate-400">/m</span></p>
                      {c.jours_restants != null && c.urgence !== "ok" && (
                        <p className="text-xs text-slate-500 mt-0.5"><Clock className="w-3 h-3 inline mr-1" />{c.jours_restants}j</p>
                      )}
                    </div>
                    <StatusBadge statut={c.statut} urgence={c.urgence} />
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
